import { CustomFieldExtensionSchema, FieldExtensionComponentProps } from "@backstage/plugin-scaffolder-react";
import React, { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {CircularProgress, Typography, TextField, Checkbox
} from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { useAsync } from "react-use";
import { FieldValidation } from '@rjsf/utils';


const prettify = (str: string): string => {
  return str
    .replace(/_/g, ' ')               // Replace underscores with spaces
    .replace(/:/g, ' - ')             // Replace colons with dashes
    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize words
};

interface PickerOption {
  label: string;
  value: string;
}

export function DynamicFieldPicker({
    onChange,
    rawErrors,
    required,
    formData,
    schema: { title, description },
    uiSchema,
}: FieldExtensionComponentProps<string | string[]>): JSX.Element {
  const endpoint = uiSchema?.['ui:options']?.engPortalEndpoint as string;
  const labelKey = uiSchema?.['ui:options']?.labelKey as string;
  const valueKey = uiSchema?.['ui:options']?.valueKey as string;
  const valueTemplate = uiSchema?.['ui:options']?.valueTemplate as string | undefined;
  const multiple = uiSchema?.['ui:options']?.multiple as boolean ?? false;
  const rawLabels = uiSchema?.['ui:options']?.rawLabels as boolean ?? false;
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const { value: options, loading, error } = useAsync(async (): Promise<PickerOption[]> => {
    if (!endpoint) return [];

      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(`${backendUrl}/${endpoint}`, {
          headers: {
              Authorization: `Bearer ${token}`,
          },
      });
      if (!response.ok) {
          throw new Error(`API error: ${response.statusText}`);
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : ( data.items || []);
        return list.map((item: any) => {
      if (typeof item === 'string') {
        return { label: rawLabels ? item : prettify(item), value: item };
      }
      
      return {
        label: item[labelKey || 'name'] || item[valueKey || 'id'], 
        value: valueTemplate
          ? valueTemplate.replace(/\{(\w+)\}/g, (_: string, key: string) => String(item[key] ?? ''))
          : String(item[valueKey || 'id']),
      };
    });
  }, [configApi, identityApi, backendUrl, endpoint, labelKey, valueKey, valueTemplate, rawLabels]);

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return (
            <Typography color="error">
                Error loading options: {error.message}
            </Typography>
        );
    }

    if (multiple) {
      const selected = Array.isArray(formData) ? formData : [];
      const selectedOptions = (options || []).filter(o => selected.includes(o.value));

      return (
        <Autocomplete
          multiple
          blurOnSelect
          loading={loading}
          options={options || []}
          getOptionLabel={(option) => option.label}
          getOptionSelected={(option, value) => option.value === value.value}
          value={selectedOptions}
          onChange={(_, selections) => {
            onChange(selections.map(s => s.value));
          }}
          noOptionsText="No options found"
          renderOption={(option, { selected: isSelected }) => (
            <>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={isSelected}
              />
              {option.label}
            </>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={title}
              helperText={description}
              variant="outlined"
              // In multiple mode the text input stays empty (values render as chips),
              // so only apply the native `required` attribute while nothing is
              // selected — otherwise the browser shows "Please fill out this field"
              // even after chips are chosen.
              required={required && selected.length === 0}
              error={!!rawErrors?.length}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    return (
      <Autocomplete
        loading={loading}
        blurOnSelect
        options={options || []}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, value) => option.value === (typeof value === 'string' ? value : value.value)}
        value={options?.find(o => o.value === formData) || null}
        onChange={(_, selection) => {
          onChange(selection?.value || '');
        }}
        noOptionsText={error ? `Error: ${(error as Error).message}` : "No options found"}
        renderInput={(params) => (
          <TextField
            {...params}
            label={title}
            helperText={description}
            variant="outlined"
            required={required}
            error={!!rawErrors?.length}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
}
export const DynamicFieldPickerSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    title: 'Dynamic Field Picker UI Options',
    properties: {
      engPortalEndpoint: {
        type: 'string',
        title: 'Eng Portal Backend API Endpoint',
        description: 'The API path to fetch data from (e.g. api/catalog/tasks).',
      },
      labelKey: {
        type: 'string',
        title: 'Label Key',
        description: 'JSON key to use for display (e.g. "name")',
      },
      valueKey: {
        type: 'string',
        title: 'Value Key',
        description: 'JSON key to use for the ID (e.g. "id")',
      },
      multiple: {
        type: 'boolean',
        title: 'Multiple Selection',
        description: 'Allow selecting multiple values (returns string[])',
      },
      rawLabels: {
        type: 'boolean',
        title: 'Raw Labels',
        description: 'Show labels as-is without prettifying (capitalizing, replacing underscores)',
      },
    },
  },
  returnValue: {
    type: 'string',
    title: 'Selected option',
  },
};

export const DynamicFieldPickerValidation = (
  _formData: string | string[],
  _validation: FieldValidation,
): void => {
  // Intentionally does not flag empty values: whether a selection is mandatory
  // is driven by the template schema's `required` array (RJSF flags required
  // empty fields on its own). Erroring on empty here would break optional
  // pickers (e.g. the "Update Existing Announcement" scope selector).
};
